/**
 * RespirationWaveformGenerator — v10.3.0
 *
 * Curva respiratoria (equivalente a la señal de impedancia torácica que
 * los monitores clínicos muestran como 3ra onda).
 *
 * Parámetros:
 *   fr                        — frecuencia respiratoria (rpm)
 *   patron_respiratorio       — 'normal' | 'rapido_superficial' | 'kussmaul'
 *                               | 'cheyne_stokes' | 'irregular' | 'apnea'
 *   respiratory_amplitude     — 0.2..2 (default 1)
 *
 * FR=0 o patrón='apnea' → línea casi plana con drift lento (apnea).
 * Baseline siempre en 0.
 */
import { sampleArtifact } from './artifacts';

function normalBreath(phase) {
  // Fase 0..1: inhalación 0..0.42 (sube), pausa 0.42..0.5, exhalación 0.5..0.95 (baja),
  // pausa espiratoria 0.95..1.
  if (phase < 0.42) {
    return Math.sin((phase / 0.42) * (Math.PI / 2));
  }
  if (phase < 0.5) {
    return 1.0;
  }
  if (phase < 0.95) {
    return Math.cos(((phase - 0.5) / 0.45) * (Math.PI / 2));
  }
  return 0;
}

function kussmaulBreath(phase) {
  // Amplitud muy grande, sin pausas — respiración profunda.
  return Math.sin(phase * Math.PI * 2) * 0.5 + 0.5;
}

function shallowBreath(phase) {
  // Rápida y superficial: patrón coseno con amplitud reducida (aplicada
  // por el llamador — devolvemos morfología en [0..1])
  return (Math.sin(phase * Math.PI * 2) * 0.5 + 0.5) * 0.55;
}

/**
 * Muestrea la onda respiratoria en `timeMs`.
 * Valor devuelto en unidades de canvas (rango ~ -18..+18) alrededor de 0.
 */
export function sampleRespiration(timeMs, cfg) {
  const { rr, respPattern, respAmp, noise } = cfg;

  // Apnea explícita o FR=0
  if (respPattern === 'apnea' || !rr || rr <= 0) {
    const { offset } = sampleArtifact(timeMs, cfg._vitals || {}, 'resp');
    return offset * 0.5;
  }

  // Frecuencia base
  let effRr = Math.max(2, Math.min(60, rr));
  let amplitude = 16 * respAmp * (noise?.signalAttenuation ?? 1);
  let phaseValue;

  const period = 60000 / effRr;
  const phase = ((timeMs % period) + period) % period / period;

  switch (respPattern) {
    case 'kussmaul':
      amplitude *= 1.6;
      phaseValue = kussmaulBreath(phase);
      break;
    case 'rapido_superficial':
      amplitude *= 0.55;
      phaseValue = shallowBreath(phase);
      break;
    case 'cheyne_stokes': {
      // Ciclos de crescendo-decrescendo con pausas de apnea (~40s de ciclo)
      const macroPeriod = 40000;
      const macroPhase = (timeMs % macroPeriod) / macroPeriod;
      const envelope = macroPhase < 0.75
        ? Math.sin(macroPhase / 0.75 * Math.PI)  // crescendo-decrescendo
        : 0;                                     // pausa apneica
      phaseValue = normalBreath(phase) * envelope;
      break;
    }
    case 'irregular': {
      // Frecuencia con jitter
      const j = Math.sin(timeMs * 0.0003) * 0.35;
      const irregPeriod = period * (1 + j);
      const irregPhase = ((timeMs % irregPeriod) + irregPeriod) % irregPeriod / irregPeriod;
      phaseValue = normalBreath(irregPhase);
      break;
    }
    case 'normal':
    default:
      phaseValue = normalBreath(phase);
      break;
  }

  // Centrar en 0: la respiración va de -amp/2 a +amp/2 alrededor del
  // baseline (más natural visualmente que 0..amp)
  const value = (phaseValue - 0.5) * amplitude * 2;

  const { offset } = sampleArtifact(timeMs, cfg._vitals || {}, 'resp');
  return value + offset;
}

export const RESPIRATORY_PATTERNS = [
  'normal', 'rapido_superficial', 'kussmaul', 'cheyne_stokes', 'irregular', 'apnea',
];
